import { Box, Flex } from "@rebass/grid";
import { useGetInline } from "@rentiohq/shared-frontend/dist/hooks/api.hooks";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { IMissingKYC } from "@rentiohq/shared-frontend/dist/types/payment.types";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  ActionCard,
  Button,
  Card,
  DisplayText,
  ESpacings,
  Grid,
  Loading,
} from "@rentiohq/web-shared/dist/components";
import { useToggle } from "@rentiohq/web-shared/dist/hooks/useToggle";
import React from "react";
import * as t from "../../../../../../services/translationService";
import { CollectionFollowUpScaModal } from "../CollectionFollowUpV2/CollectionFollowUpScaModal";

const PAYERS_TO_SHOW_WHEN_COLLAPSED = 6;

interface IProps {
  propertyId: number;
}

/**
 * DEV-3205. Mangopay refuses a payer's transfer when that payer is an OWNER who has not
 * given SCA proxy consent. The payer gets a card on their own dashboard, but until this
 * existed nothing told the broker: the property page showed a rent paid in full while
 * the money sat in the wallet, unable to move.
 *
 * Deliberately its own section rather than a row inside "Beneficiary verification": that
 * list is payees, and its KYC branch is not something a tenant can act on. The card and
 * the SCA modal are the same ones, so both sections look and behave alike.
 */
export const ScaFollowUp = (props: IProps) => {
  const { propertyId } = props;

  // State
  const [showAllPayers, toggleShowAllPayers] = useToggle(false);
  const [scaContactToPerform, setScaContactToPerform] = React.useState<{
    contactId: number;
    bankAccountId: number;
  }>();

  // Data
  const { data, isFetching, fetchData } = useGetInline<IMissingKYC[]>({
    path: `/properties/${propertyId}/sca-blocked-payers`,
    isV2: true,
  });

  const items = data || [];

  // Event handlers
  const handleClose = () => {
    setScaContactToPerform(undefined);
  };

  const handleScaSuccess = () => {
    handleClose();
    fetchData(false);
  };

  const handleSca = (item: IMissingKYC) => {
    // `== null`, not a truthy check: `bankAccountId` is a number and 0 is a value, not an absence.
    if (item.bankAccountId == null || !item.contact) {
      showAlert({
        type: "error",
        message: getLocalizedText("payments.sca_follow_up.error.no_account"),
      });

      return;
    }

    setScaContactToPerform({
      contactId: item.contact.id,
      bankAccountId: item.bankAccountId,
    });
  };

  // Render
  if (items.length === 0) {
    return null;
  }

  return (
    <>
      <Card>
        <Flex mb={ESpacings.base} alignItems="center">
          <DisplayText space="none" size="medium">
            {getLocalizedText("payments.sca_follow_up.heading")}
          </DisplayText>

          {isFetching && (
            <Box ml={ESpacings.tight}>
              <Loading asDots={true} />
            </Box>
          )}
        </Flex>

        <Grid>
          {items
            .slice(
              0,
              showAllPayers ? items.length : PAYERS_TO_SHOW_WHEN_COLLAPSED,
            )
            .map((item, index) => (
              // Both ids are optional on IMissingKYC, and two rows that both fall back to
              // `undefined` would collide on React's normalized "" key and reconcile wrongly.
              <Grid.Item
                key={item.bankAccountId ?? item.contact?.id ?? index}
                width={[1, 1, 1 / 3]}
              >
                <ActionCard
                  appearance="error"
                  heading={
                    <Flex alignItems="center">
                      {item.contact && (
                        <Box flex={1} mr={ESpacings.tight}>
                          <DisplayText size="small">
                            {getName(item.contact)}
                          </DisplayText>
                        </Box>
                      )}
                    </Flex>
                  }
                  actions={
                    item.contact
                      ? [
                          [
                            {
                              content: t.contactViewContactAction(),
                              url: `/contacts/${item.contact.id}`,
                            },
                          ],
                        ]
                      : []
                  }
                  // Payer-worded key of its own rather than the beneficiary section's:
                  // the Dutch of that one names the payee, which is the wrong actor on
                  // this card. Fixed rather than derived from `scaStatus` too -
                  // this section exists precisely because the stored status can say
                  // ENROLLED while Mangopay refuses the transfer, and keying off it would
                  // render a missing translation for exactly the case being reported.
                  reason={getLocalizedText(
                    "payments.sca_follow_up.reason.consent_required",
                  )}
                  primaryActions={[
                    {
                      content: getLocalizedText(
                        "payments.sca_follow_up.cta.start",
                      ),
                      onClick: () => handleSca(item),
                      color: "red",
                      appearance: "outline",
                    },
                  ]}
                />
              </Grid.Item>
            ))}
        </Grid>

        {items.length > PAYERS_TO_SHOW_WHEN_COLLAPSED && !showAllPayers && (
          <Button appearance="link" onClick={toggleShowAllPayers}>
            {`${getLocalizedText("system.show_all")} (${items.length})`}
          </Button>
        )}
      </Card>

      {scaContactToPerform && (
        <CollectionFollowUpScaModal
          contactId={scaContactToPerform.contactId}
          bankAccountId={scaContactToPerform.bankAccountId}
          dismissable={true}
          onSuccess={handleScaSuccess}
          onClose={handleClose}
        />
      )}
    </>
  );
};
